import type { FederatedLabHit } from '~/utils/mapFederatedSearchLabResponse'
import { getFederatedSearchItemListIds } from '~/components/FederatedSearch/gtmItemLists'
import { sendSelectListItem } from '~/composables/useGtmEvents'
import type { EventDescription } from '~/composables/types/event'
import type { FederatedSearchAnalyticsPlacement } from '~/utils/searchEngineHelpers'
import type { FederatedSearchOption } from './items'

// GA4 item lists for the Typesense federated search — same list ids, payload
// and rules as the Algolia one (FederatedSearch/gtmItemLists.ts), adapted to
// what FederatedSearchTypesensePanel.vue renders.

// Mirrors the panel's `shownEvents`: the desktop dropdown caps to the FIRST
// `maxEvents`, the mobile overlay shows everything.
export const getVisibleTypesenseEventItems = (
  events: FederatedLabHit[],
  maxEvents?: number
) => (maxEvents == null ? events : events.slice(0, maxEvents))

// Mirrors the panel's `showLastViewed`: with an empty query the events column
// is replaced by last viewed. Those load async on first open, so an empty
// query only counts as visible once loading finished and returned nothing.
export const isTypesenseEventsColumnVisible = ({
  query,
  lastViewedEvents,
  isLastViewedReady,
}: {
  query: string
  lastViewedEvents: EventDescription[]
  isLastViewedReady: boolean
}) => Boolean(query.trim()) || (isLastViewedReady && !lastViewedEvents.length)

// GA4 `select_item` for an event/kit or last viewed row. Called from the
// component's single `onSelect` (click and Enter share it), before the hard
// navigation so the list attribution is persisted for the rest of the funnel.
export const sendTypesenseFederatedSelectItem = (
  option: FederatedSearchOption,
  {
    events,
    lastViewedEvents,
    query,
    placement,
  }: {
    events: FederatedLabHit[]
    lastViewedEvents: EventDescription[]
    query: string
    placement?: FederatedSearchAnalyticsPlacement
  }
) => {
  const itemListIds = getFederatedSearchItemListIds(placement)
  if (option.kind === 'event') {
    const searchTerm = query.trim()
    sendSelectListItem(
      itemListIds.results,
      [option.item],
      Math.max(events.indexOf(option.item), 0),
      searchTerm ? { item_search_term: searchTerm } : {}
    )
  } else if (option.kind === 'lastViewed') {
    sendSelectListItem(
      itemListIds.lastViewed,
      [option.item],
      Math.max(lastViewedEvents.indexOf(option.item), 0)
    )
  }
}
